/**
 * General Ledger Screen — M_GeneralLedger CRUD
 * Split: Left Form (70%) + Right Chart of Accounts Tree (30%)
 * Header center-right: Ledger Code
 * Main: Ledger Name*, Group Name* (search+add), Ledger Type* (dropdown), Branch* (dropdown)
 * Collapsible "More": Display Name, Check Print, Alias, Opening Balance + Dr/Cr
 * Tabs: Additional Info (address/contact/tax fields) | Budget Info (annual/monthly + fiscal year 12-month grid)
 */
import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { Save, Grid3X3, XCircle, Search, Plus, ChevronDown, ChevronUp, RefreshCw, ArrowLeft } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import ChartOfAccountsTree from '../../components/erp/ChartOfAccountsTree';
import ERPLookupModal from '../../components/erp/ERPLookupModal';
import DynamicFieldRenderer from '../../components/erp/DynamicFieldRenderer';
import { GlobalizationBar, DEFAULT_GLOBALIZATION } from '../../components/erp/GlobalizationBar';
import { erpToast } from '../../components/erp/ERPToast';
import { FormHelp } from '../../components/help-engine/FormHelp';
import { HelpInfo } from '../../components/help-engine/HelpInfo';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken} from '../../lib/api';

const tkn = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' });
const enterNav = (e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); const f = e.target.closest('form'); if (!f) return; const els = Array.from(f.querySelectorAll('input:not([disabled]):not([readonly]),select:not([disabled])')); const i = els.indexOf(e.target); if (i >= 0 && i < els.length - 1) els[i + 1].focus(); } };

const MONTHS_FY = ['April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March'];
const MONTHS_CY = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Primary Group codes → human labels & BS/PL category
const PG_LABELS = { '01': 'Assets', '02': 'Liabilities', '03': 'Incomes', '04': 'Expenses' };
const PG_CATEGORY = {
  '01': 'BS', 'assets': 'BS', 'asset': 'BS', 'fixed assets': 'BS', 'current assets': 'BS',
  '02': 'BS', 'liabilities': 'BS', 'liability': 'BS', 'current liabilities': 'BS', 'capital': 'BS',
  '03': 'PL', 'incomes': 'PL', 'income': 'PL', 'revenue': 'PL', 'sales': 'PL',
  '04': 'PL', 'expenses': 'PL', 'expense': 'PL', 'cost': 'PL', 'overheads': 'PL',
};
const deriveCategoryFromPG = (pgCode, pgName) => {
  if (!pgCode) return '';
  const key = String(pgCode).trim().toLowerCase();
  if (PG_CATEGORY[key]) return PG_CATEGORY[key];
  if (pgName) { const nk = pgName.trim().toLowerCase(); if (PG_CATEGORY[nk]) return PG_CATEGORY[nk]; }
  // Partial match
  if (key.includes('asset') || key.includes('liabilit') || key.includes('capital') || key.includes('bank') || key.includes('cash')) return 'BS';
  if (key.includes('income') || key.includes('expense') || key.includes('revenue') || key.includes('cost')) return 'PL';
  return '';
};
const pgLabel = (code) => PG_LABELS[String(code).trim()] || String(code || '');

const EMPTY_FORM = {
  GLCode: '', GLName: '', GroupName: '', GroupId: '', GLType: 'General Ledger', Branch: 'main branch',
  DisplayName: '', CheckPrint: '', Alias: '', OpeningBalance: '0.00', DrCr: 'Dr', PlaceOfHead: '0',
  Category: '',  // BS or PL — auto-derived from Primary Group
  // Additional Info
  Address: '', Country: '', State: '', City: '', StateCode: '', PIN: '', JobTitle: '', Department: '', Office: '',
  Phone1: '', Phone2: '', Mobile: '', Fax: '', IPPhone: '', IncomeTaxNo: '', GSTNo: '', TOTNo: '', PANNo: '',
  // Budget Info
  BudgetType: 'annual', AnnualBudget: '0.00', MonthlyBudget: '0.00',
  BudgetYear: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
  FiscalYearType: 'financial',
  Apr: '0.00', May: '0.00', Jun: '0.00', Jul: '0.00', Aug: '0.00', Sep: '0.00', Oct: '0.00', Nov: '0.00', Dec: '0.00', Jan: '0.00', Feb: '0.00', Mar: '0.00'
};

const F = memo(({ label, value, onChange, required, disabled, type = 'text', testId, style, fieldId }) => (
  <div className="erp-form-row">
    <label className={`erp-form-label${required ? ' required' : ''}`}>{label}{fieldId && <HelpInfo controlId={fieldId} controlName={label} />}</label>
    <div className="erp-form-control">
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="erp-input" data-testid={testId} data-help-control={fieldId} onKeyDown={enterNav} style={style} />
    </div>
  </div>
));

// ═══════════════════════ GRID ═══════════════════════
const GL_COLUMNS = [
  { field: 'GLId', header: 'GL Code' },
  { field: 'GLName', header: 'GL Name' },
  { field: 'GroupId', header: 'Group' },
  { field: 'GlType', header: 'Type' },
  { field: 'DrCurBal', header: 'Dr Bal' },
  { field: 'CrCurBal', header: 'Cr Bal' },
];

const GeneralLedgerGrid = ({ onAddNew, onEdit, onBack }) => {
  const [allItems, setAllItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [treeKey, setTreeKey] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/finance/chart-of-accounts`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) { setAllItems(j.data?.generalLedgers || []); }
    } catch { erpToast.error('Failed to load'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const { data, filteredTotal } = useMemo(() => {
    let items = [...allItems];
    Object.entries(filters).forEach(([key, val]) => {
      if (val) { const s = val.toLowerCase(); items = items.filter(i => String(i[key] || '').toLowerCase().includes(s)); }
    });
    const filteredTotal = items.length;
    if (sortField) {
      items.sort((a, b) => {
        if (sortField === 'DrCurBal' || sortField === 'CrCurBal') {
          return sortDir === 'asc' ? (a[sortField] || 0) - (b[sortField] || 0) : (b[sortField] || 0) - (a[sortField] || 0);
        }
        const av = String(a[sortField] || ''), bv = String(b[sortField] || '');
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return { data: items.slice((page - 1) * pageSize, page * pageSize), filteredTotal };
  }, [allItems, filters, sortField, sortDir, page, pageSize]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Void this General Ledger?')) return;
    try {
      const r = await fetch(`${API_URL}/api/finance/general-ledgers/${id}`, { method: 'DELETE', headers: hdrs() });
      const j = await r.json();
      if (j.success) { erpToast.success('General Ledger voided'); loadData(); setTreeKey(k => k + 1); }
      else erpToast.error(j.error || 'Void failed');
    } catch { erpToast.error('Failed to void'); }
  };

  const fmtCurrency = (v) => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  return (
    <div className="erp-module" style={{ flexDirection: 'row' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <ERPAdvancedGrid
          title="General Ledger"
          columns={GL_COLUMNS}
          data={data}
          total={filteredTotal}
          idField="GLId"
          nameField="GLName"
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
          filters={filters}
          onFilterChange={(f) => { setFilters(f); setPage(1); }}
          loading={loading}
          onAddNew={onAddNew}
          onEdit={onEdit}
          onVoid={(row) => handleDelete(row.GLId)}
          testId="general-ledger"
          onBack={onBack}
          exportFileName="GeneralLedger"
          cellRenderer={(field, value) => {
            if (field === 'DrCurBal' || field === 'CrCurBal') return <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 11 }}>{fmtCurrency(value)}</span>;
            return null;
          }}
          cellClassName={(field) => {
            if (field === 'DrCurBal' || field === 'CrCurBal') return 'text-right';
            return '';
          }}
        />
      </div>
      <div style={{ width: 280, flexShrink: 0 }}><ChartOfAccountsTree refreshKey={treeKey} onSelect={(n) => { if (n.type === 'general') { const item = allItems.find(d => d.GLId === n.code); if (item) onEdit(item, 'edit'); } }} /></div>
    </div>
  );
};

// ═══════════════════════ FORM ═══════════════════════
const GeneralLedgerForm = ({ editData, mode, onGridView }) => {
  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [tab, setTab] = useState('additional');
  const [moreOpen, setMoreOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lookupOpen, setLookupOpen] = useState(false);
  const [groupLedgers, setGroupLedgers] = useState([]);
  const [primaryGroups, setPrimaryGroups] = useState([]);
  const [ledgerTypes, setLedgerTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [treeKey] = useState(0);
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();
  const token = tkn();
  const [dynBasic, setDynBasic] = useState({});
  const [dynMore, setDynMore] = useState({});
  const [dynAdditional, setDynAdditional] = useState({});
  const [dynBudget, setDynBudget] = useState({});
  const [globalConfig, setGlobalConfig] = useState(DEFAULT_GLOBALIZATION);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Derive BS/PL category from selected group
  const getCategoryFromGroup = (groupId) => {
    const gl = groupLedgers.find(g => g.GroupId === groupId);
    const pgCode = gl?.PrimaryGroup;
    if (pgCode) {
      const pg = primaryGroups.find(p => p.PGroupId === pgCode);
      if (pg?.Category) return pg.Category;
      return deriveCategoryFromPG(pgCode, pg?.PGroupName);
    }
    // Try deriving from the group name directly
    if (gl?.GroupName) return deriveCategoryFromPG('', gl.GroupName);
    return '';
  };

  const handleGroupSelect = (gId, gName) => {
    set('GroupId', gId);
    set('GroupName', gName);
    const cat = getCategoryFromGroup(gId);
    if (cat) set('Category', cat);
  };

  useEffect(() => {
    if (editData) {
      setForm({
        ...EMPTY_FORM,
        GLCode: editData.GLId || '', GLName: editData.GLName || '', GroupId: editData.GroupId || '', GroupName: editData.GroupId || '',
        GLType: editData.GlType || 'General Ledger', 
        OpeningBalance: String(editData.DrOpenBal || editData.CrOpenBal || '0.00'),
        DrCr: (editData.DrOpenBal || 0) > 0 ? 'Dr' : 'Cr',
        DisplayName: editData.DisplayName || '', CheckPrint: editData.CheckPrint || '',
        Alias: editData.Alias1 || '', Address: editData.Address || '',
        State: editData.State || '', PIN: editData.PinCode || '',
        IncomeTaxNo: editData.IncomeTaxNo || '', GSTNo: editData.VAT || '',
        TOTNo: editData.TinNo || '', PANNo: editData.PANNo || '',
        BranchId: editData.Branchid || '', Branch: editData.Branchid || ''
      });
    } else { setForm({ ...EMPTY_FORM }); }
  }, [editData]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/finance/chart-of-accounts`, { headers: hdrs() });
        const j = await r.json();
        if (j.success) { setGroupLedgers(j.data?.groupLedgers || []); setPrimaryGroups(j.data?.primaryGroups || []); }
      } catch {}
      try {
        const r2 = await fetch(`${API_URL}/api/finance/ledger-types`, { headers: hdrs() });
        const j2 = await r2.json();
        if (j2.success) setLedgerTypes(j2.data || []);
      } catch {}
      try {
        const r3 = await fetch(`${API_URL}/api/finance/dropdown/branches`, { headers: hdrs() });
        const j3 = await r3.json();
        if (j3.success) setBranches(j3.data || []);
      } catch {}
    };
    load();
  }, []);

  const handleSave = async () => {
    if (!form.GLName?.trim()) { setError('Ledger Name is required'); return; }
    if (!form.GroupId) { setError('Group Name is required'); return; }
    setSaving(true); setError('');
    try {
      const isEditMode = (isEdit || mode === 'edit') && form.GLCode;
      const url = isEditMode ? `${API_URL}/api/finance/general-ledgers/${form.GLCode}` : `${API_URL}/api/finance/general-ledgers`;
      const method = isEditMode ? 'PUT' : 'POST';
      const payload = {
        GLName: form.GLName, GroupId: form.GroupId, GlType: form.GLType, 
        DrOpenBal: form.DrCr === 'Dr' ? parseFloat(form.OpeningBalance) || 0 : 0,
        CrOpenBal: form.DrCr === 'Cr' ? parseFloat(form.OpeningBalance) || 0 : 0,
        Alias1: form.Alias || '', Alias2: '', Alias3: '',
        Address: form.Address || '', City: form.City || '', State: form.State || '',
        StateCode: form.StateCode || '', PinCode: form.PIN || '',
        JobTitle: form.JobTitle || '', Department: form.Department || '', Office: form.Office || '',
        Phone1: form.Phone1 || '', Phone2: form.Phone2 || '', Mobile: form.Mobile || '',
        Fax: form.Fax || '', IPPhone: form.IPPhone || '',
        IncomeTaxNo: form.IncomeTaxNo || '', VAT: form.GSTNo || '', TinNo: form.TOTNo || '', PANNo: form.PANNo || '',
        GDefault: '', AliasId: '', Branchid: form.BranchId || form.Branch || '',
        CheckPrint: form.CheckPrint || '', DisplayName: form.DisplayName || '',
        BudgetAmount: form.BudgetType === 'annual' ? parseFloat(form.AnnualBudget) || 0 : parseFloat(form.MonthlyBudget) || 0,
        BudgetYear: form.BudgetYear || ''
      };
      const r = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(payload) });
      const j = await r.json();
      if (j.success) {
        erpToast.success(j.message || (isEditMode ? 'General Ledger updated' : 'General Ledger saved'));
        if (j.GLId) setForm(p => ({ ...p, GLCode: j.GLId }));
        if (isEditMode) setMode('add');
      } else { setError(j.error || 'Save failed'); }
    } catch (e) { setError('Failed to save'); }
    setSaving(false);
  };

  const months = form.FiscalYearType === 'financial' ? MONTHS_FY : MONTHS_CY;
  const monthKeys = form.FiscalYearType === 'financial'
    ? ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <FormHelp formName="GeneralLedger" userRole={userRole}>
    <div className="erp-module" style={{ flexDirection: 'row' }} data-dev-component="GeneralLedger" data-dev-file="screens/finance/GeneralLedgerScreen.js">
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header with Ledger Code center-right */}
        <div className="erp-screen-header">
          <button onClick={onGridView} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>General Ledger Creation</span></button>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <div data-testid="genl-code-display" style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--erp-font-mono)', color: 'var(--erp-primary)', background: 'var(--erp-hover)', padding: '2px 12px', borderRadius: 4 }}>
              {form.GLCode || 'Auto-Generated'}
            </div>
          </div>
          <div className="erp-screen-actions">
            {!isView && <button data-testid="genl-save-btn" className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving}>{mode === 'edit' ? <RefreshCw size={13} /> : <Save size={13} />} {saving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}</button>}
            <button className="erp-btn erp-btn-ghost" onClick={onGridView}><Grid3X3 size={13} /> Gridview</button>
            {!isView && <button className="erp-btn erp-btn-ghost" onClick={() => { setForm({ ...EMPTY_FORM }); setError(''); }}><XCircle size={13} /> Clear</button>}
          </div>
        </div>
        {error && <div className="erp-alert-error">{error}</div>}

        <form style={{ flex: 1, overflow: 'auto', padding: 'var(--erp-sp-3)' }} onSubmit={e => e.preventDefault()}>
          {/* ─── Main Fields ─── */}
          <div className="erp-form-card" style={{ marginBottom: 8 }}>
            <div className="erp-form-section">
              <div className="erp-form-3col">
                <div>
                  <F label="Ledger Name" value={form.GLName} onChange={v => set('GLName', v)} required disabled={isView} testId="genl-name" fieldId="GLName" />
                  <div className="erp-form-row">
                    <label className="erp-form-label required">Ledger Type<HelpInfo controlId="GLType" controlName="Ledger Type" /></label>
                    <div className="erp-form-control">
                      <select data-testid="genl-type" data-help-control="GLType" value={form.GLType} onChange={e => set('GLType', e.target.value)} disabled={isView} className="erp-input erp-select">
                        <option value="General Ledger">General Ledger</option>
                        {ledgerTypes.map(t => <option key={t.id || t} value={t.name || t}>{t.name || t}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="erp-form-row">
                    <label className="erp-form-label required">Group Name<HelpInfo controlId="GroupName" controlName="Group Name" /></label>
                    <div className="erp-form-control" style={{ display: 'flex', gap: 4 }}>
                      <input data-testid="genl-group" data-help-control="GroupName" value={form.GroupName} readOnly className="erp-input" style={{ flex: 1 }} />
                      <button className="erp-btn-icon" style={{ border: '1px solid var(--erp-border)' }} onClick={() => setLookupOpen(true)} disabled={isView}><Search size={12} /></button>
                      <button className="erp-btn-icon" style={{ border: '1px solid var(--erp-border)' }} disabled={isView}><Plus size={12} /></button>
                    </div>
                  </div>
                  {/* Category — auto-derived from Primary Group */}
                  <div className="erp-form-row">
                    <label className="erp-form-label">Category<HelpInfo controlId="Category" controlName="Category (BS/PL)" /></label>
                    <div className="erp-form-control">
                      <div data-testid="genl-category" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input value={form.Category || ''} readOnly className="erp-input" style={{ width: 60, textAlign: 'center', fontWeight: 700, background: 'var(--erp-hover)', color: form.Category === 'BS' ? 'var(--erp-primary)' : form.Category === 'PL' ? 'var(--erp-success)' : 'var(--erp-text-muted)' }} placeholder="Auto" />
                        {form.Category && <span style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>{form.Category === 'BS' ? 'Balance Sheet' : 'Profit & Loss'}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="erp-form-row">
                    <label className="erp-form-label required">Branch<HelpInfo controlId="Branch" controlName="Branch" /></label>
                    <div className="erp-form-control">
                      <select data-testid="genl-branch" data-help-control="Branch" value={form.BranchId || form.Branch} onChange={e => { set('BranchId', e.target.value); set('Branch', e.target.options[e.target.selectedIndex].text); }} disabled={isView} className="erp-input erp-select">
                        <option value="">-- Select Branch --</option>
                        {branches.map(b => <option key={b.ID} value={b.ID}>{b.Name} {b.Location ? `- ${b.Location}` : ''}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <F label="Place of Head" value={form.PlaceOfHead} onChange={v => set('PlaceOfHead', v)} disabled={isView} testId="genl-poh" type="number" fieldId="PlaceOfHead" />
                </div>
              </div>
            </div>
          </div>
          <DynamicFieldRenderer screenName="GeneralLedger_Basic" recordId={form.GLCode} token={token} values={dynBasic} onChange={(k, v) => setDynBasic(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />

          {/* ─── Collapsible "More" Section ─── */}
          <div className="erp-form-card" style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', cursor: 'pointer', borderBottom: moreOpen ? '1px solid var(--erp-border)' : 'none' }}
              onClick={() => setMoreOpen(!moreOpen)} data-testid="genl-more-toggle">
              {moreOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              <span style={{ fontSize: 11, fontWeight: 600 }}>More</span>
            </div>
            {moreOpen && (
              <div className="erp-form-section" style={{ padding: 'var(--erp-sp-3)' }}>
                <div className="erp-form-3col">
                  <F label="Display Name" value={form.DisplayName} onChange={v => set('DisplayName', v)} disabled={isView} testId="genl-dispname" fieldId="DisplayName" />
                  <F label="Check Print" value={form.CheckPrint} onChange={v => set('CheckPrint', v)} disabled={isView} testId="genl-checkprint" fieldId="CheckPrint" />
                  <F label="Alias" value={form.Alias} onChange={v => set('Alias', v)} disabled={isView} testId="genl-alias" fieldId="Alias" />
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginTop: 8 }}>
                  <div style={{ flex: 1, maxWidth: 200 }}>
                    <F label="Opening Balance" value={form.OpeningBalance} onChange={v => set('OpeningBalance', v)} disabled={isView} testId="genl-openbal" type="number" fieldId="OpeningBalance" />
                  </div>
                  <div style={{ display: 'flex', gap: 12, paddingBottom: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <input type="radio" name="drcr" value="Dr" checked={form.DrCr === 'Dr'} onChange={() => set('DrCr', 'Dr')} disabled={isView} /> Dr
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <input type="radio" name="drcr" value="Cr" checked={form.DrCr === 'Cr'} onChange={() => set('DrCr', 'Cr')} disabled={isView} /> Cr
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DynamicFieldRenderer screenName="GeneralLedger_More" recordId={form.GLCode} token={token} values={dynMore} onChange={(k, v) => setDynMore(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />

          {/* ─── Tabs: Additional Info / Budget Info / Globalization ─── */}
          <div className="erp-form-card">
            <div className="erp-tabs" data-testid="genl-tabs" style={{ borderBottom: '1px solid var(--erp-border)' }}>
              <button className={`erp-tab${tab === 'additional' ? ' active' : ''}`} onClick={() => setTab('additional')} data-testid="tab-additional">Additional Info</button>
              <button className={`erp-tab${tab === 'budget' ? ' active' : ''}`} onClick={() => setTab('budget')} data-testid="tab-budget">Budget Info</button>
              <button className={`erp-tab${tab === 'globalization' ? ' active' : ''}`} onClick={() => setTab('globalization')} data-testid="tab-globalization">Globalization</button>
            </div>

            {/* Additional Info Tab */}
            {tab === 'additional' && (<>
              <div className="erp-form-section" style={{ padding: 'var(--erp-sp-3)' }}>
                <div className="erp-form-3col">
                  <div>
                    <F label="Address" value={form.Address} onChange={v => set('Address', v)} disabled={isView} testId="genl-address" />
                    <F label="Country" value={form.Country} onChange={v => set('Country', v)} disabled={isView} testId="genl-country" />
                    <F label="State" value={form.State} onChange={v => set('State', v)} disabled={isView} testId="genl-state" />
                    <F label="City" value={form.City} onChange={v => set('City', v)} disabled={isView} testId="genl-city" />
                    <F label="State Code" value={form.StateCode} onChange={v => set('StateCode', v)} disabled={isView} testId="genl-statecode" />
                    <F label="PIN" value={form.PIN} onChange={v => set('PIN', v)} disabled={isView} testId="genl-pin" />
                  </div>
                  <div>
                    <F label="Job Title" value={form.JobTitle} onChange={v => set('JobTitle', v)} disabled={isView} testId="genl-jobtitle" />
                    <F label="Department" value={form.Department} onChange={v => set('Department', v)} disabled={isView} testId="genl-department" />
                    <F label="Office" value={form.Office} onChange={v => set('Office', v)} disabled={isView} testId="genl-office" />
                    <F label="Phone 1" value={form.Phone1} onChange={v => set('Phone1', v)} disabled={isView} testId="genl-phone1" />
                    <F label="Phone 2" value={form.Phone2} onChange={v => set('Phone2', v)} disabled={isView} testId="genl-phone2" />
                    <F label="Mobile" value={form.Mobile} onChange={v => set('Mobile', v)} disabled={isView} testId="genl-mobile" />
                  </div>
                  <div>
                    <F label="Fax" value={form.Fax} onChange={v => set('Fax', v)} disabled={isView} testId="genl-fax" />
                    <F label="IP Phone" value={form.IPPhone} onChange={v => set('IPPhone', v)} disabled={isView} testId="genl-ipphone" />
                    <F label="IncomeTax No" value={form.IncomeTaxNo} onChange={v => set('IncomeTaxNo', v)} disabled={isView} testId="genl-itno" />
                    <F label="GST No" value={form.GSTNo} onChange={v => set('GSTNo', v)} disabled={isView} testId="genl-gstno" />
                    <F label="TOT No" value={form.TOTNo} onChange={v => set('TOTNo', v)} disabled={isView} testId="genl-totno" />
                    <F label="PAN No" value={form.PANNo} onChange={v => set('PANNo', v)} disabled={isView} testId="genl-panno" />
                  </div>
                </div>
              </div>
              <DynamicFieldRenderer screenName="GeneralLedger_Additional" recordId={form.GLCode} token={token} values={dynAdditional} onChange={(k, v) => setDynAdditional(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />
            </>)}

            {/* Budget Info Tab */}
            {tab === 'budget' && (<>
              <div className="erp-form-section" style={{ padding: 'var(--erp-sp-3)' }}>
                {/* Budget Type Toggle */}
                <div style={{ display: 'flex', gap: 20, marginBottom: 12, alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    <input type="radio" name="budgettype" value="annual" checked={form.BudgetType === 'annual'} onChange={() => set('BudgetType', 'annual')} disabled={isView} /> Annual Budget
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    <input type="radio" name="budgettype" value="monthly" checked={form.BudgetType === 'monthly'} onChange={() => set('BudgetType', 'monthly')} disabled={isView} /> Monthly Budget
                  </label>
                  <div style={{ marginLeft: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
                    <F label="Budget Value" value={form.BudgetType === 'annual' ? form.AnnualBudget : form.MonthlyBudget}
                      onChange={v => set(form.BudgetType === 'annual' ? 'AnnualBudget' : 'MonthlyBudget', v)}
                      disabled={isView} testId="genl-budgetval" type="number" />
                    <div className="erp-form-row" style={{ margin: 0 }}>
                      <label className="erp-form-label" style={{ minWidth: 90 }}>Budget Year<HelpInfo controlId="BudgetYear" controlName="Budget Year" /></label>
                      <div className="erp-form-control">
                        <input data-testid="genl-budgetyear" value={form.BudgetYear || ''} onChange={e => set('BudgetYear', e.target.value)} disabled={isView} className="erp-input" placeholder="e.g. 2025-2026" style={{ width: 110 }} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fiscal Year Details */}
                <div style={{ borderTop: '1px solid var(--erp-border)', paddingTop: 10, marginTop: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700 }}>Fiscal Year Details</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, marginLeft: 16 }}>
                      <input type="radio" name="fytype" value="financial" checked={form.FiscalYearType === 'financial'} onChange={() => set('FiscalYearType', 'financial')} disabled={isView} /> Financial Year
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                      <input type="radio" name="fytype" value="calendar" checked={form.FiscalYearType === 'calendar'} onChange={() => set('FiscalYearType', 'calendar')} disabled={isView} /> Calendar Year
                    </label>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 }}>
                    {months.map((m, i) => (
                      <div key={m} className="erp-form-row" style={{ margin: 0 }}>
                        <label className="erp-form-label" style={{ fontSize: 10, minWidth: 60 }}>{m}</label>
                        <div className="erp-form-control">
                          <input data-testid={`genl-fy-${monthKeys[i]}`} type="number" value={form[monthKeys[i]] || '0.00'}
                            onChange={e => set(monthKeys[i], e.target.value)} disabled={isView}
                            className="erp-input" style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 11 }} onKeyDown={enterNav} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DynamicFieldRenderer screenName="GeneralLedger_Budget" recordId={form.GLCode} token={token} values={dynBudget} onChange={(k, v) => setDynBudget(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />
            </>)}

            {/* Globalization Tab */}
            {tab === 'globalization' && (
              <div className="erp-form-section" style={{ padding: 'var(--erp-sp-3)' }}>
                <GlobalizationBar config={globalConfig} onChange={setGlobalConfig} token={token} />
              </div>
            )}
          </div>
        </form>
      </div>
      <div style={{ width: 280, flexShrink: 0 }}>
        <ChartOfAccountsTree selectedId={form.GLCode} refreshKey={treeKey} onSelect={(n) => {
          if (n.type === 'group') { handleGroupSelect(n.code, n.label); }
        }} />
      </div>
      {lookupOpen && (
        <ERPLookupModal
          title="Select Group Ledger"
          data={groupLedgers.map(g => ({ id: g.GroupId, name: g.GroupName, extra: pgLabel(g.PrimaryGroup) }))}
          columns={[{ field: 'id', label: 'Code' }, { field: 'name', label: 'Name' }, { field: 'extra', label: 'Primary Group' }]}
          onSelect={(item) => { handleGroupSelect(item.id, item.name); setLookupOpen(false); }}
          onClose={() => setLookupOpen(false)}
        />
      )}
    </div>
    </FormHelp>
  );
};

// ═══════════════════════ MAIN ═══════════════════════
const GeneralLedgerScreen = ({ theme = 'light', navigateTo = () => {} }) => {
  const [view, setView] = useState('grid');
  const [editData, setEditData] = useState(null);
  const [formMode, setFormMode] = useState('add');

  if (view === 'form') {
    return <GeneralLedgerForm editData={editData} mode={formMode} onGridView={() => { setView('grid'); setEditData(null); }} />;
  }
  return <GeneralLedgerGrid
    onAddNew={() => { setEditData(null); setFormMode('add'); setView('form'); }}
    onEdit={(row, m) => { setEditData(row); setFormMode(m || 'edit'); setView('form'); }}
    onBack={() => navigateTo('HUB')} />;
};

export default GeneralLedgerScreen;
